'use client'

import { useState, useEffect } from 'react'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/useToast'
import { Property, Review } from '@/types'
import { getApiUrl } from '@/lib/apiUrl'

interface PropertyReviewsManagementProps {
  landlordId: string
}

interface ReviewWithProperty extends Review {
  property?: Property
  reviewer?: {
    firstName?: string
    lastName?: string
    email?: string
  }
}

export default function PropertyReviewsManagement({ landlordId }: PropertyReviewsManagementProps) {
  const { token } = useUser()
  const toast = useToast()
  const [reviews, setReviews] = useState<ReviewWithProperty[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedProperty, setSelectedProperty] = useState<string>('all')
  const [properties, setProperties] = useState<Property[]>([])
  const [approvedApplications, setApprovedApplications] = useState<any[]>([])
  const [showClientReviewForm, setShowClientReviewForm] = useState(false)
  const [selectedClientForReview, setSelectedClientForReview] = useState<any>(null)
  const [clientReviewRating, setClientReviewRating] = useState(5)
  const [clientReviewTitle, setClientReviewTitle] = useState('')
  const [clientReviewComment, setClientReviewComment] = useState('')
  const [submittingClientReview, setSubmittingClientReview] = useState(false)

  useEffect(() => {
    if (landlordId && token) {
      loadData()
    }
  }, [landlordId, token])

  const loadData = async () => {
    if (!token) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

      // Fetch landlord's properties
      const propsResponse = await fetch(`${apiUrl}/properties?landlordId=${landlordId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (propsResponse.ok) {
        const propsData = await propsResponse.json()
        const propsArray = Array.isArray(propsData) ? propsData : (propsData.properties || [])
        setProperties(propsArray)
      }

      // Fetch reviews for landlord's properties
      const reviewsResponse = await fetch(`${apiUrl}/reviews/landlord/${landlordId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (reviewsResponse.ok) {
        const reviewsData = await reviewsResponse.json()
        setReviews(reviewsData.data || reviewsData || [])
      }

      // Fetch approved applications (clients landlord can review)
      const appsResponse = await fetch(`${apiUrl}/applications?status=approved`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (appsResponse.ok) {
        const appsData = await appsResponse.json()
        const appsArray = Array.isArray(appsData) ? appsData : (appsData.applications || [])
        // Filter to only applications for this landlord
        const landlordApps = appsArray.filter((app: any) => {
          const appLandlordId = (app.landlord as any)?._id || (app.landlord as any)?.id || (app as any).landlord_id || (app as any).landlord
          return appLandlordId?.toString() === landlordId?.toString()
        })
        setApprovedApplications(landlordApps)
      }
    } catch (error) {
      console.error('Error loading reviews:', error)
      toast.error('Failed to load reviews')
    } finally {
      setLoading(false)
    }
  }

  const getRatingStars = (rating: number) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown date'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Helper to get a string ID from populated object or raw id
  const toId = (v: any): string => {
    if (v == null) return ''
    if (typeof v === 'string') return v.trim()
    const id = (v && (v._id ?? v.id))
    return id ? String(id) : ''
  }

  // Submit client review
  const submitClientReview = async () => {
    if (!selectedClientForReview || !clientReviewTitle?.trim() || !clientReviewComment?.trim()) {
      toast.error('Please fill in all required fields')
      return
    }

    const title = clientReviewTitle.trim()
    const comment = clientReviewComment.trim()
    if (title.length < 5) {
      toast.error('Review title must be at least 5 characters')
      return
    }
    if (comment.length < 10) {
      toast.error('Review text must be at least 10 characters')
      return
    }

    if (!token) {
      toast.error('Please log in to submit a review')
      return
    }

    const clientId = toId(selectedClientForReview.client)
    const propertyId = toId(selectedClientForReview.property)
    const applicationId = toId(selectedClientForReview) || toId(selectedClientForReview.id) || (selectedClientForReview as any)?._id

    if (!clientId || !propertyId) {
      toast.error('Unable to determine client or property. Please close and select the client again.')
      return
    }

    setSubmittingClientReview(true)
    try {
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/reviews/client`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId,
          propertyId,
          ...(applicationId ? { applicationId } : {}),
          rating: clientReviewRating,
          title,
          comment
        })
      })

      const data = await response.json()

      if (!response.ok) {
        const message =
          data?.message ||
          (Array.isArray(data?.errors) && data.errors[0]?.msg) ||
          (typeof data?.error === 'string' ? data.error : null) ||
          'Failed to submit review'
        throw new Error(message)
      }

      toast.success('Client review submitted successfully!')
      setShowClientReviewForm(false)
      setSelectedClientForReview(null)
      setClientReviewRating(5)
      setClientReviewTitle('')
      setClientReviewComment('')
      loadData()
    } catch (error) {
      console.error('Error submitting client review:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to submit review')
    } finally {
      setSubmittingClientReview(false)
    }
  }

  const filteredReviews = selectedProperty === 'all'
    ? reviews
    : reviews.filter(review => {
        const propertyId = (review.property as any)?._id || (review.property as any)?.id || review.property_id
        return propertyId === selectedProperty
      })

  // Calculate stats
  const stats = {
    total: reviews.length,
    averageRating: reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0,
    byProperty: properties.reduce((acc, prop) => {
      const propId = (prop as any)._id || prop.id
      const propReviews = reviews.filter(r => {
        const rPropId = (r.property as any)?._id || (r.property as any)?.id || r.property_id
        return rPropId === propId
      })
      acc[propId] = {
        count: propReviews.length,
        average: propReviews.length > 0
          ? propReviews.reduce((sum, r) => sum + r.rating, 0) / propReviews.length
          : 0
      }
      return acc
    }, {} as Record<string, { count: number; average: number }>)
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="space-y-3">
              <div className="h-20 bg-gray-200 rounded"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-display text-stone-900">Property Reviews</h2>
          <p className="text-stone-600 mt-1">View and respond to tenant reviews</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <h3 className="text-lg font-medium text-stone-900 mb-2">Total Reviews</h3>
          <p className="text-3xl font-display text-emerald-600">{stats.total}</p>
          <p className="text-sm text-stone-500">All properties</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <h3 className="text-lg font-medium text-stone-900 mb-2">Average Rating</h3>
          <p className="text-3xl font-display text-amber-600">{stats.averageRating.toFixed(1)}</p>
          <p className="text-sm text-stone-500">{getRatingStars(Math.round(stats.averageRating))}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <h3 className="text-lg font-medium text-stone-900 mb-2">Properties Reviewed</h3>
          <p className="text-3xl font-display text-blue-600">
            {Object.values(stats.byProperty).filter(p => p.count > 0).length}
          </p>
          <p className="text-sm text-stone-500">Out of {properties.length} properties</p>
        </div>
      </div>

      {/* Filter by Property */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
        <label className="block text-sm font-medium text-stone-700 mb-2">
          Filter by Property
        </label>
        <select
          value={selectedProperty}
          onChange={(e) => setSelectedProperty(e.target.value)}
          className="w-full px-4 py-2 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-stone-900"
        >
          <option value="all">All Properties</option>
          {properties.map((property) => {
            const propId = (property as any)._id || property.id
            const propStats = stats.byProperty[propId]
            return (
              <option key={propId} value={propId}>
                {property.title} - {property.location} ({propStats?.count || 0} reviews)
              </option>
            )
          })}
        </select>
      </div>

      {/* Reviews List */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200">
        <div className="px-6 py-4 border-b border-stone-200">
          <h3 className="text-lg font-medium text-stone-900">
            {selectedProperty === 'all' ? 'All Reviews' : 'Property Reviews'}
          </h3>
        </div>

        {filteredReviews.length > 0 ? (
          <div className="divide-y divide-stone-200">
            {filteredReviews.map((review) => {
              const reviewId = (review as any)._id || review.id
              const reviewer = review.reviewer
              const reviewerName = reviewer?.firstName && reviewer?.lastName
                ? `${reviewer.firstName} ${reviewer.lastName}`
                : reviewer?.email?.split('@')[0] || 'Anonymous'
              const isAnonymous = (review as any).isAnonymous || (review as any).is_anonymous
              const propertyTitle = review.property?.title || 'Unknown Property'

              return (
                <div key={reviewId} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-stone-900">{review.title}</h4>
                        <div className="flex text-yellow-400 text-sm">{getRatingStars(review.rating)}</div>
                      </div>

                      <div className="text-sm text-stone-600 space-y-1 mb-3">
                        <p>
                          <span className="font-medium">Property:</span> {propertyTitle}
                        </p>
                        <p>
                          <span className="font-medium">Reviewer:</span>{' '}
                          {isAnonymous ? 'Anonymous' : reviewerName}
                        </p>
                        <p>
                          <span className="font-medium">Date:</span>{' '}
                          {formatDate((review as any).createdAt || (review as any).created_at)}
                        </p>
                      </div>

                      <p className="text-stone-700 leading-relaxed mb-4">
                        {review.comment || (review as any).review_text}
                      </p>

                      {/* Landlord Response */}
                      {(review as any).landlordResponse ? (
                        <div className="mt-4 pl-4 border-l-4 border-emerald-200 bg-emerald-50 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-semibold text-emerald-900">Your Response</span>
                            <span className="text-xs text-emerald-700">
                              {formatDate((review as any).landlordResponse.respondedAt)}
                            </span>
                          </div>
                          <p className="text-sm text-emerald-800">
                            {(review as any).landlordResponse.comment}
                          </p>
                        </div>
                      ) : (
                        <div className="mt-4 text-sm text-stone-500 italic">
                          No response yet. Consider responding to show you value tenant feedback.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="p-12 text-center text-stone-500">
            <div className="text-5xl mb-4">⭐</div>
            <p className="text-lg font-medium mb-2">No reviews yet</p>
            <p className="text-sm">
              {selectedProperty === 'all'
                ? 'Reviews from tenants will appear here once they submit them.'
                : 'This property has no reviews yet.'}
            </p>
          </div>
        )}
      </div>

      {/* Client Review Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200">
        <div className="px-6 py-4 border-b border-stone-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-stone-900">Review Your Tenants</h3>
            <p className="text-sm text-stone-600 mt-1">Review clients who have rented your properties</p>
          </div>
          {approvedApplications.length > 0 && (
            <button
              onClick={() => {
                if (approvedApplications.length === 0) {
                  toast.error('No approved applications available for review')
                  return
                }
                setShowClientReviewForm(true)
              }}
              className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold"
            >
              Review Client
            </button>
          )}
        </div>

        {approvedApplications.length === 0 ? (
          <div className="p-12 text-center text-stone-500">
            <div className="text-5xl mb-4">👥</div>
            <p className="text-lg font-medium mb-2">No clients to review yet</p>
            <p className="text-sm">Once clients rent your properties, you can review them here.</p>
          </div>
        ) : (
          <div className="p-6">
            <p className="text-sm text-stone-600 mb-4">
              You have {approvedApplications.length} approved application{approvedApplications.length !== 1 ? 's' : ''} available for review.
            </p>
            <p className="text-xs text-stone-500">
              Click "Review Client" above to submit a review for a tenant.
            </p>
          </div>
        )}
      </div>

      {/* Client Review Form Modal */}
      {showClientReviewForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Review Client</h3>
                <button
                  onClick={() => {
                    setShowClientReviewForm(false)
                    setSelectedClientForReview(null)
                    setClientReviewRating(5)
                    setClientReviewTitle('')
                    setClientReviewComment('')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Client *
                  </label>
                  <select
                    value={selectedClientForReview?.id || (selectedClientForReview as any)?._id || ''}
                    onChange={(e) => {
                      const app = approvedApplications.find(a => (a.id || (a as any)._id) === e.target.value)
                      setSelectedClientForReview(app)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  >
                    <option value="">Select a client</option>
                    {approvedApplications.map((app) => {
                      const appId = app.id || (app as any)._id
                      const client = app.client
                      const clientName = client?.firstName && client?.lastName
                        ? `${client.firstName} ${client.lastName}`
                        : client?.email || 'Unknown Client'
                      const propertyTitle = app.property?.title || 'Unknown Property'
                      return (
                        <option key={appId} value={appId}>
                          {clientName} - {propertyTitle}
                        </option>
                      )
                    })}
                  </select>
                </div>

                {selectedClientForReview && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rating *
                      </label>
                      <div className="flex items-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setClientReviewRating(star)}
                            className={`text-2xl ${
                              star <= clientReviewRating ? 'text-yellow-500' : 'text-gray-300'
                            }`}
                          >
                            ⭐
                          </button>
                        ))}
                        <span className="text-sm text-gray-500 ml-2">
                          {clientReviewRating} out of 5
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Review Title *
                      </label>
                      <input
                        type="text"
                        value={clientReviewTitle}
                        onChange={(e) => setClientReviewTitle(e.target.value)}
                        placeholder="Brief summary (min 5 characters)"
                        minLength={5}
                        maxLength={200}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-0.5">{clientReviewTitle.length}/200</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Review Text *
                      </label>
                      <textarea
                        value={clientReviewComment}
                        onChange={(e) => setClientReviewComment(e.target.value)}
                        placeholder="Share your experience (min 10 characters)..."
                        rows={4}
                        minLength={10}
                        maxLength={2000}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-0.5">{clientReviewComment.length}/2000 (min 10)</p>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <button
                        onClick={submitClientReview}
                        disabled={submittingClientReview}
                        className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50"
                      >
                        {submittingClientReview ? 'Submitting...' : 'Submit Review'}
                      </button>
                      <button
                        onClick={() => {
                          setShowClientReviewForm(false)
                          setSelectedClientForReview(null)
                          setClientReviewRating(5)
                          setClientReviewTitle('')
                          setClientReviewComment('')
                        }}
                        className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

